import { call, takeEvery, select, put } from 'redux-saga/effects';
import { propEq, prop } from 'ramda';
import { history } from '../store';
import {
  signUserIn,
  logUserIn,
  resetPassword,
  confirmNewPassword,
  fetchRedirectUri,
  checkUserSession
} from './util';
import { retrieveUriParam, LocalStorage, updateUserLastAction } from '../../utils/helpers';
import { selectUsername, selectPassword } from './selectors';
import { clearMessage, showError } from '../alert';
import {
  LOGIN_REQUEST,
  NEW_PASSWORD_REQUEST,
  RESET_PASSWORD_REQUEST,
  FORGOT_PASSWORD_CONFIRM,
  SESSION_REQUEST,
  setPasswordResetStatus
} from './index';

function* attemptLogin({
  payload: {
    username,
    password,
    new_password = '',
    redirectUri = retrieveUriParam('redirect_uri'),
    onFailure = () => null
  }
}) {
  const clientId = retrieveUriParam('client_id');
  try {
    const response = yield call(signUserIn, { username, password, new_password });
    const localUsers = LocalStorage.getArray('usernames');
    if (!localUsers.includes(username.toLowerCase())) {
      localUsers.push(username.toLowerCase());
      LocalStorage.setObject('usernames', localUsers);
    }

    LocalStorage.set('user.username', username.toLowerCase());
    if (propEq('newPasswordRequired', false)(response)) {
      logUserIn({ username, userTokens: prop('userTokens', response), redirectUri });
    } else {
      history.push(`/new-password?client_id=${clientId}&redirect_uri=${redirectUri}`);
    }
  } catch (error) {
    onFailure();
    yield put(
      showError({
        text: error,
        type: 'LoginFailure'
      })
    );
  }
}

function* attemptChangePassword({ payload: { new_password, onFailure = () => null } }) {
  const redirectUri = retrieveUriParam('redirect_uri');
  const username = yield select(selectUsername);
  const password = yield select(selectPassword);
  try {
    const response = yield call(signUserIn, { username, password, new_password });
    logUserIn({ username, userTokens: prop('userTokens', response), redirectUri });
  } catch (error) {
    onFailure();
    yield put(showError({ text: error, type: 'PasswordChangeFailure' }));
  }
}

function* attemptResetPassword({ payload: { email, onFailure = () => null } }) {
  const username = yield email || select(selectUsername);
  try {
    const response = yield call(resetPassword, { username });
    const redirectUri = retrieveUriParam('redirect_uri');
    if (redirectUri) {
      yield updateUserLastAction({
        email: username,
        redirectUri: retrieveUriParam('redirect_uri')
      });
    }
    yield put(clearMessage());
    yield put(setPasswordResetStatus({ passwordReset: response.passwordReset }));
  } catch (error) {
    onFailure();
    yield put(showError({ text: error, type: 'InvalidEmailError' }));
  }
}

function* attemptForgotPasswordConfirmation({
  payload: { email, password, onFailure = () => null }
}) {
  try {
    yield call(confirmNewPassword, { username: email, password });

    const redirectUri = yield fetchRedirectUri(email);

    yield call(attemptLogin, { payload: { username: email, password, redirectUri } });
  } catch (error) {
    onFailure();
    yield put(
      showError({
        text: error,
        type: 'CognitoError'
      })
    );
  }
}

function* attemptCheckUserSession() {
  yield call(checkUserSession);
}

const authSagas = [
  takeEvery(LOGIN_REQUEST, attemptLogin),
  takeEvery(NEW_PASSWORD_REQUEST, attemptChangePassword),
  takeEvery(RESET_PASSWORD_REQUEST, attemptResetPassword),
  takeEvery(FORGOT_PASSWORD_CONFIRM, attemptForgotPasswordConfirmation),
  takeEvery(SESSION_REQUEST, attemptCheckUserSession)
];

export default authSagas;



// WEBPACK FOOTER //
// ./src/core/auth/sagas.js